// components/ShortcutSectionItem.tsx
import { omit } from 'lodash';
import { UseFormRegister, FieldValues, useFormContext, useWatch } from 'react-hook-form';

import Input from '../../../components/form/Input';
import { IConfigItemType } from '../validates/sectionBuilderSchema';

import EbookBundleSectionItem, { IPropsEbookBundleItem } from './EbookBundleSectionItem';
import EbookCollectionSectionItem, { IPropsEbookCollectionItem } from './EbookCollectionSectionItem';
import EbookSectionItem, { IPropsEbookItem } from './EbookSectionItem';
import ExploreEbookCollectionSectionItem, { IPropsExploreEbookCollection } from './ExploreEbookCollectionSectionItem';
import ExploreSectionItemV2, { IPropsExploreSectionItemV2 } from './ExploreSectionItemV2';
import FeedSectionItem, { IPropsFeedItem } from './FeedSectionItem';
import LinkSectionItem, { IPropsLinkItem } from './LinkSectionItem';
import PintoCustomLinkSectionItem, { IPropsPintoCustomLinkItem } from './PintoCustomLinkSectionItem';
import ProductSetSectionItem, { IPropsProductSetItem } from './ProductSetSectionItem';
import SearchSectionItem, { IPropsSearchItem } from './SearchSectionItem';
import UserProfileSectionItem, { IPropsUserProfileItem } from './UserProfileSectionItem';

export const toBase = (
  d: IPropsShortcutItem,
):
  | IPropsEbookItem
  | IPropsEbookBundleItem
  | IPropsProductSetItem
  | IPropsLinkItem
  | IPropsSearchItem
  | IPropsExploreSectionItemV2
  | IPropsUserProfileItem
  | IPropsFeedItem
  | IPropsEbookCollectionItem
  | IPropsExploreEbookCollection
  | IPropsPintoCustomLinkItem => {
  switch (d.type) {
    case 'ebook-shortcut':
      return { type: IConfigItemType.Ebook, ...omit(d, ['type']) } as IPropsEbookItem;
    case 'ebook-bundle-shortcut':
      return { type: IConfigItemType.EbookBundle, ...omit(d, ['type']) } as IPropsEbookBundleItem;
    case 'product-set-shortcut':
      return { type: IConfigItemType.ProductSet, ...omit(d, ['type']) } as IPropsProductSetItem;
    case 'link-shortcut':
      return { type: IConfigItemType.Link, ...omit(d, ['type']) } as IPropsLinkItem;
    case 'search-shortcut':
      return { type: IConfigItemType.Search, ...omit(d, ['type']) } as IPropsSearchItem;
    case 'explore-shortcut':
    case 'explore-bundle-shortcut':
      return omit(d, ['type']) as IPropsExploreSectionItemV2;
    case 'user-profile-shortcut':
      return { type: IConfigItemType.UserProfile, ...omit(d, ['type']) } as IPropsUserProfileItem;
    case 'pinto-feed-shortcut':
      return { type: IConfigItemType.Feed, ...omit(d, ['type']) } as IPropsFeedItem;
    case 'ebook-collection-shortcut':
      return { type: IConfigItemType.EbookCollection, ...omit(d, ['type']) } as IPropsEbookCollectionItem;
    case 'explore-ebook-collection-shortcut':
      return { type: IConfigItemType.ExploreEbookCollection, ...omit(d, ['type']) } as IPropsExploreEbookCollection;
    case 'pinto-custom-link':
      return { type: IConfigItemType.PintoCustomLink, ...omit(d, ['type']) } as IPropsPintoCustomLinkItem;
    default:
      throw new Error(`Unsupported shortcut type: ${d.type}`);
  }
};

type ShortcutMeta = {
  label: string;
  dayImageUrl: string;
  darkImageUrl: string;
};

type Shortcutize<T extends { type: string }> = Omit<T, 'type'> &
  ShortcutMeta & {
    type: `${T['type']}-shortcut`;
  };
type IExploreBase = IPropsExploreSectionItemV2 & { type: 'explore' };
type IExploreBundleBase = IPropsExploreSectionItemV2 & { type: 'explore-bundle' };
type PintoFeedShortcut = Omit<IPropsFeedItem, 'type'> & ShortcutMeta & { type: 'pinto-feed-shortcut' };
type IExploreEbookCollectionBase = IPropsExploreEbookCollection & { type: 'explore-ebook-collection' };
type PintoCustomLinkShortcut = IPropsPintoCustomLinkItem & ShortcutMeta;

export type IPropsShortcutItem =
  | Shortcutize<IPropsEbookItem>
  | Shortcutize<IPropsEbookBundleItem>
  | Shortcutize<IPropsProductSetItem>
  | Shortcutize<IPropsLinkItem>
  | Shortcutize<IPropsSearchItem>
  | Shortcutize<IExploreBase>
  | Shortcutize<IExploreBundleBase>
  | Shortcutize<IPropsUserProfileItem>
  | Shortcutize<IPropsEbookCollectionItem>
  | Shortcutize<IExploreEbookCollectionBase>
  | PintoFeedShortcut
  | PintoCustomLinkShortcut;

interface IPropsShortcutSectionItem {
  index: number;
  itemData: IPropsShortcutItem;
  register: UseFormRegister<FieldValues>;
  onDeleteSectionItem?: (idx?: number) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const COMPONENT_MAP: Record<IPropsShortcutItem['type'], React.ComponentType<any>> = {
  'ebook-shortcut': EbookSectionItem,
  'ebook-bundle-shortcut': EbookBundleSectionItem,
  'product-set-shortcut': ProductSetSectionItem,
  'link-shortcut': LinkSectionItem,
  'search-shortcut': SearchSectionItem,
  'explore-shortcut': ExploreSectionItemV2,
  'explore-bundle-shortcut': ExploreSectionItemV2,
  'pinto-feed-shortcut': FeedSectionItem,
  'user-profile-shortcut': UserProfileSectionItem,
  'ebook-collection-shortcut': EbookCollectionSectionItem,
  'explore-ebook-collection-shortcut': ExploreEbookCollectionSectionItem,
  'pinto-custom-link': PintoCustomLinkSectionItem,
} as const;

export default function ShortcutSectionItem(props: IPropsShortcutSectionItem) {
  const { index, itemData, register } = props;

  const { control } = useFormContext();
  const dayImageValue = useWatch({ control, name: `configItems.${index}.dayImageUrl` }) as string;
  const darkImageValue = useWatch({ control, name: `configItems.${index}.darkImageUrl` }) as string;

  const Meta = (
    <>
      <div className="flex items-center space-x-2">
        <Input label="Label" name={`configItems.${index}.label`} placeholder="Shortcut label" register={register} required />
      </div>

      <div className="flex items-center space-x-2">
        {dayImageValue ? <img src={dayImageValue} alt="Day Preview" className="w-16 h-16 object-cover rounded-full" /> : <div className="w-16 h-16 rounded-full bg-gray-100" />}
        <Input label="Day Image URL" name={`configItems.${index}.dayImageUrl`} placeholder="https://day-image.jpg" register={register} required />
      </div>

      {/* ── Dark Image URL ── */}
      <div className="flex items-center space-x-2">
        {darkImageValue ? <img src={darkImageValue} alt="Dark Preview" className="w-16 h-16 object-cover rounded-full" /> : <div className="w-16 h-16 rounded-full bg-gray-100" />}
        <Input label="Dark Image URL" name={`configItems.${index}.darkImageUrl`} placeholder="https://dark-image.jpg" register={register} required />
      </div>
    </>
  );

  const BaseComponent = COMPONENT_MAP[itemData.type];
  const baseProps = toBase(itemData);

  return (
    <div className="border rounded-lg p-0 space-y-4">
      <h4 className="pl-8 text-18 font-semibold">{itemData.type}</h4>
      <div className="flex justify-between">{Meta}</div>

      <BaseComponent index={index} itemData={baseProps} register={register} isShowOptionalFields={false} />
    </div>
  );
}
